import React, { useEffect, useState, useMemo } from 'react';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  query,
  where,
  addDoc,
  setDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import Navbar from '../components/Navbar';
import { updateAppointment } from '../helpers/appointment';
import { formatTime } from '../helpers/datesAndTimes';
import { LuRefreshCw } from 'react-icons/lu';
import StaffUsersTable from '../components/StaffUsersTable';
import ClientInfoModal from '../components/modals/ClientInfoModal';
import StaffInfoModal from '../components/modals/StaffInfoModal';
import CreateStaffModal from '../components/modals/CreateStaffModal';
import {
  format,
  isToday,
  parse,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  addMonths,
  subMonths,
  differenceInYears,
  differenceInCalendarDays,
} from 'date-fns';
import { useAuth } from '../AuthContext';
import { getClientByPhone, sendTextMessage } from '../Helpers';
import { updateSMSNotificationGroup } from '../helpers/client';
import {
  getAnnouncement,
  newAnnouncement,
  endAnnouncement,
  dismissAnnouncementForUser,
} from '../helpers/announcement';
import UnifiedChatbox from '../components/UnifiedChatbox';
import CreateClientModal from '../components/modals/CreateClientModal';
import {
  createNewClient,
  updateClient,
  sendConfidentialityDisclaimer,
} from '../helpers/client';

const generateVerificationToken = () => {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
};

const normalizeName = (s) => {
  if (!s) return '';
  const trimmed = s.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';

  return trimmed
    .split(' ')
    .map(word =>
      word
        .split('-')
        .map(part => {
          if (!part) return '';
          const lower = part.toLowerCase();
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join('-')
    )
    .join(' ');
};

const isoToSlashes = (value) => {
  if (!value) return '';
  const [yyyy, mm, dd] = value.split('-');
  if (!yyyy || !mm || !dd) return '';
  return `${mm}/${dd}/${yyyy}`;
};

const getEmptyNewClientForm = () => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  dob: '',
  menstrual: '',
  language: 'English',
  adminNotes: '',
  notes: '',
});

const idsToAddresses = {
  0: "JMJ Orlando Clinic",
  1: "JMJ Kissimmee Clinic"
};

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const locationDays = {
  Orlando: [1, 3, 5],
  Kissimmee: [2, 4, 6],
};

const toDateSafe = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate(); // Firestore Timestamp
  if (typeof v === 'string') return new Date(v);          // ISO string fallback
  return new Date(v);                                      // Date or ms epoch
};

// Unconfirmed appointments helper: status 0 + confirmed:false
const isApptUnconfirmed = (app) => app?.status === 0 && app?.confirmed === false;
const isApptConfirmed = (app) => app?.confirmed !== false; // undefined => treat as confirmed (legacy)

const Staff = () => {
  const [selectedTab, setSelectedTab] = useState('Users');
  const [clients, setClients] = useState([]);
  const [showNonAbortionMinded, setShowNonAbortionMinded] = useState(false);
  const [userViewMode, setUserViewMode] = useState('clients'); // 'clients' | 'staff'
  const [appointments, setAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('Orlando');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedOpenSlot, setSelectedOpenSlot] = useState(null);
  const [closures, setClosures] = useState([]);
  const [closureReason, setClosureReason] = useState('');
  const [noLanguages, setNoLanguages] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientForm, setClientForm] = useState({});
  const [locationName, setLocationName] = useState('');
  const [chatPartner, setChatPartner] = useState(null); // { id, firstName, lastName }
  const [showChatModal, setShowChatModal] = useState(false);
  const [showStaffInfoModal, setShowStaffInfoModal] = useState(false);
  const [viewStaffUser, setViewStaffUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState(getEmptyNewClientForm());
  const [selectedClientLatestAppt, setSelectedClientLatestAppt] = useState(null);

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementBodyInput, setAnnouncementBodyInput] = useState('');
  const [announcementTextStaffInput, setAnnouncementTextStaffInput] = useState(true);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  // NEW: unread counts state
  const [staffUnreadCounts, setStaffUnreadCounts] = useState({});
  const [staffUnreadTotal, setStaffUnreadTotal] = useState(0);

  const relevantDays = locationDays[selectedLocation];
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7'
  }[relevantDays.length] || 'grid-cols-1';

  const recentStarredPhones = useMemo(() => {
    const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000);
    const phones = new Set();

    for (const app of appointments) {
      const createdAt = toDateSafe(app.createdAt) || toDateSafe(app.createdAtMinute);
      const updatedAt = toDateSafe(app.updatedAt) || toDateSafe(app.updatedAtMinute);
      const rescheduledAt = toDateSafe(app.rescheduledAt) || updatedAt;

      const recentlyBooked = app.status === 0 && createdAt && createdAt >= twelveHoursAgo;
      const recentlyRescheduled = app.status === 4 && rescheduledAt && rescheduledAt >= twelveHoursAgo;

      if (recentlyBooked || recentlyRescheduled) {
        if (app.client) phones.add(app.client.toString());
      }
    }

    return phones;
  }, [appointments]);

  const hasAppointmentsOnDateAtLocation = (dateObj, location) => {
    const target = format(dateObj, 'yyyy-MM-dd');
    return appointments.some(app => {
      const appDate = parse(app.date, 'MM/dd/yyyy', new Date());
      return (
        format(appDate, 'yyyy-MM-dd') === target &&
        app.location === location
      );
    });
  };

  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const baseYear = new Date().getFullYear();
  const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => baseYear - 2 + i);

  const parseDob = (dobStr) => {
    if (!dobStr) return null;
    const p1 = parse(dobStr, 'MM/dd/yyyy', new Date());
    if (!isNaN(p1)) return p1;
    const p2 = new Date(dobStr);
    if (!isNaN(p2)) return p2;
    return null;
  };

  const getAgeFromDob = (dobStr) => {
    const dob = parseDob(dobStr);
    if (!dob) return null;
    return differenceInYears(new Date(), dob);
  };

  const getGestationWeeksFromLMP = (lmpStr) => {
    const lmp = parseDob(lmpStr);
    if (!lmp) return null;

    const today = new Date();
    const days = differenceInCalendarDays(today, lmp);

    if (days < 0) return null;

    const fullWeeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    return Number(`${fullWeeks}.${remainingDays}`);
  };

  const [showCreateStaffModal, setShowCreateStaffModal] = useState(false);
  const [newStaffFirstName, setNewStaffFirstName] = useState('');
  const [newStaffLastName, setNewStaffLastName] = useState('');
  const [newStaffPhone, setNewStaffPhone] = useState('');
  const [newStaffPassword, setNewStaffPassword] = useState('');
  const [newStaffSMSList, setNewStaffSMSList] = useState(false);

  const [showSMSSettingsModal, setShowSMSSettingsModal] = useState(false);
  const [smsRecipientGroup, setSmsRecipientGroup] = useState('staff'); // or 'development'

  const auth = useAuth();
  const userIsSuperstaff = auth.client?.superstaff;

  const currentStaffId =
    auth.client?.id ||
    (auth.client?.phone ? `${auth.client.phone}STAFF` : null);

  // Filter announcements by visibility + dismissed
  const visibleAnnouncements = announcements.filter(ann => {
    if (ann.visible === false) return false;
    const dismissedBy = ann.dismissed || [];
    if (!currentStaffId) return true;
    return !dismissedBy.includes(currentStaffId);
  });

  useEffect(() => {
    if (visibleAnnouncements.length === 0) {
      setCurrentAnnouncementIndex(0);
    } else if (currentAnnouncementIndex >= visibleAnnouncements.length) {
      setCurrentAnnouncementIndex(visibleAnnouncements.length - 1);
    }
  }, [visibleAnnouncements.length, currentAnnouncementIndex]);

  useEffect(() => {
    const loadSMSGroup = async () => {
      try {
        const docRef = doc(db, 'settings', 'SMS_GROUP');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const group = snap.data().group;
          setSmsRecipientGroup(group === 'development' ? 'development' : 'staff');
        }
      } catch (e) {
        console.error('Failed to load SMS group setting:', e);
      }
    };
    loadSMSGroup();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const anns = await getAnnouncement();
        setAnnouncements(anns);
      } catch (e) {
        console.error('Failed to load announcements:', e);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const appointmentsSnapshot = await getDocs(collection(db, 'appointments'));
      const allClients = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      const allAppointments = appointmentsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(app => app.status !== 5); // 5 = deleted/cleared, don't show on calendar

      const closuresSnapshot = await getDocs(collection(db, 'closures'));
      const noLangSnapshot = await getDocs(collection(db, 'noLanguages'));

      setClosures(closuresSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setNoLanguages(noLangSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setClients(allClients);
      setAppointments(allAppointments);

      (async () => {
        try {
          const anns = await getAnnouncement();
          setAnnouncements(anns);
        } catch (e) {
          console.error('Failed to load announcements:', e);
        }
      })();
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedLocation, currentDate]);

  useEffect(() => {
    const fetchLocationName = async () => {
      if (selectedAppointment) {
        const docRef = doc(db, 'locations', selectedAppointment.location.toString());
        const docSnap = await getDoc(docRef);
        const name = docSnap.exists() ? docSnap.data().name : 'Unknown Location';
        setLocationName(name);
      }
    };
    fetchLocationName();
  }, [selectedAppointment]);

  const formatPhoneNumber = phone => {
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
  };

  const getTimeSlots = () => Array.from({ length: 4 }, (_, i) => i + 10);

  const getLatestAppointmentStatus = clientId => {
    const clientAppointments = appointments.filter(app => app.client === clientId);
    if (clientAppointments.length === 0) return 'No Appointments';
    const latest = clientAppointments.reduce((a, b) => new Date(a.date) > new Date(b.date) ? a : b);
    return latest.status ?? 'Unknown';
  };

  // NEW: latest appointment helper for a phone number (prefer upcoming)
  const getLatestAppointmentForPhone = (phone) => {
    const rows = appointments.filter(a => a.client?.toString() === phone?.toString());
    if (rows.length === 0) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const withDate = rows
      .map(a => {
        const d = parse(a.date, 'MM/dd/yyyy', new Date());
        d.setHours(0, 0, 0, 0);
        return { ...a, _d: d };
      })
      .filter(a => !isNaN(a._d));

    const upcoming = withDate
      .filter(a => a._d >= today)
      .sort((a, b) => a._d - b._d || (Number(a.startTime) || 0) - (Number(b.startTime) || 0));

    if (upcoming.length > 0) return upcoming[0];

    return withDate.sort((a, b) => b._d - a._d)[0] || null;
  };

  const updateRelatedAppointments = async phoneNumber => {
    const qy = query(
      collection(db, 'appointments'),
      where('client', '==', phoneNumber),
      where('status', 'in', [0, 4])
    );
    const snapshot = await getDocs(qy);
    const updatePromises = snapshot.docs.map(docSnap => updateAppointment(docSnap.id, { status: 3 }));
    await Promise.all(updatePromises);
  };

  const handleClientUpdate = async () => {
    try {
      await updateDoc(doc(db, 'clients', selectedClient.id), clientForm);
      setShowClientModal(false);
      fetchData();
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to update client.');
    }
  };

  const handleDelete = async (clientId, phoneNumber, fullName) => {
    const isStaff = clientId.toString().endsWith("STAFF");
    const roleLabel = isStaff ? "staff member" : "client";

    const confirmed = window.confirm(
      `Are you sure you want to delete ${roleLabel} "${fullName}"?`
    );

    if (!confirmed) return;

    try {
      await updateRelatedAppointments(phoneNumber);
      await deleteDoc(doc(db, 'clients', clientId));
      setClients(prev => prev.filter(client => client.id !== clientId));
      fetchData();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleBlock = async (clientId, phoneNumber) => {
    try {
      await updateRelatedAppointments(phoneNumber);
      await updateDoc(doc(db, 'clients', clientId), { unableToAct: true });
      setClients(prevClients =>
        prevClients.map(client =>
          client.id === clientId ? { ...client, unableToAct: true } : client
        )
      );
      fetchData();
      alert('Client has been blocked.');
    } catch (err) {
      console.error('Block failed:', err);
    }
  };

  const handleUnblock = async (clientId) => {
    try {
      await updateDoc(doc(db, 'clients', clientId), { unableToAct: false });
      setClients(prevClients =>
        prevClients.map(client =>
          client.id === clientId ? { ...client, unableToAct: false } : client
        )
      );
      fetchData();
      alert('Client has been unblocked.');
    } catch (err) {
      console.error('Unblock failed:', err);
      alert('Failed to unblock client.');
    }
  };

  const filteredClients = clients.filter(client => {
    const fullName = `${client.firstName || ''} ${client.lastName || ''}`.toLowerCase();
    const phone = (client.phone?.toString() || '').toLowerCase();
    const query = searchQuery.toLowerCase();

    const matchesSearch =
      fullName.includes(query) || phone.includes(query);

    const isStaffUser = client.id?.toString().endsWith('STAFF');
    const matchesView =
      userViewMode === 'staff' ? isStaffUser : !isStaffUser;

    return matchesSearch && matchesView;
  });

  const getAppointmentsForDay = day => {
    return appointments.filter(app => {
      const appDate = parse(app.date, 'MM/dd/yyyy', new Date());
      return (
        format(appDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd') &&
        ((selectedLocation === 'Orlando' && app.location === 0) ||
          (selectedLocation === 'Kissimmee' && app.location === 1))
      );
    });
  };

  const getClientNameByPhone = phone => {
    const client = clients.find(c => c.phone === phone);
    return client ? `${client.firstName} ${client.lastName}` : 'Unknown Client';
  };

  const handleDeleteAppointment = async () => {
    try {
      let reason = '';
      if (!isPastAppointment) {
        reason = window.prompt("Please provide a reason for cancelling this appointment:") || '';
        if (!reason.trim()) {
          alert("Cancellation aborted: reason is required.");
          return;
        }
      }

      const updatePayload = { status: 3 };
      if (reason.trim()) updatePayload.rescheduleReason = reason.trim();

      await updateAppointment(selectedAppointment.id, updatePayload);

      const cancelledClient = getClientByPhone(selectedAppointment.client, clients);

      await sendTextMessage({
        message: cancelledClient.language === "Spanish"
          ? `JMJPC CITA: Su cita para las ${(parseInt(selectedAppointment.startTime) % 12 === 0 ? 12 : parseInt(selectedAppointment.startTime) % 12)} ${parseInt(selectedAppointment.startTime) >= 12 ? "PM" : "AM"} el ${selectedAppointment.date} en ${idsToAddresses[selectedAppointment.location]} ha sido cancelada.`
          : `JMJPC BOOKING: Your appointment for ${(parseInt(selectedAppointment.startTime) % 12 === 0 ? 12 : parseInt(selectedAppointment.startTime) % 12)} ${parseInt(selectedAppointment.startTime) >= 12 ? "PM" : "AM"} on ${selectedAppointment.date} at ${idsToAddresses[selectedAppointment.location]} has been cancelled.`,
        yourPhoneNumber: "4073059084",
        recipientPhoneNumber: cancelledClient.phone,
        senderName: "JMJPC"
      });

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error deleting appointment:', err);
    }
  };

  const handleDeleteAndFreeSlot = async () => {
    if (!selectedAppointment) return;

    const confirmed = window.confirm(
      'Delete this appointment and free the slot? This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      await updateAppointment(selectedAppointment.id, { status: 5 }); // 5 = deleted/cleared
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Delete & free slot failed:', err);
      alert('Failed to delete appointment.');
    }
  };

  const isSlotClosed = (date, hour, location) => {
    const dateStr = format(date, 'MM/dd/yyyy');
    return closures.find(c =>
      c.date === dateStr &&
      c.location === location &&
      c.startTime <= hour &&
      c.endTime > hour
    ) || null;
  };

  const isSpanishUnavailable = (date, location) => {
    const dateStr = format(date, 'MM/dd/yyyy');
    return noLanguages.find(nl =>
      nl.date === dateStr &&
      nl.location === location &&
      nl.language === 'Spanish'
    ) || null;
  };

  const matchingClosure = selectedOpenSlot
    ? isSlotClosed(selectedOpenSlot.date, selectedOpenSlot.hour, selectedOpenSlot.location)
    : null;

  const matchingUnavailability = selectedOpenSlot
    ? isSpanishUnavailable(selectedOpenSlot.date, selectedOpenSlot.location)
    : null;

  const selectedClientData = selectedAppointment
    ? clients.find(c => c.phone === selectedAppointment.client)
    : null;

  const selectedClientAge = selectedClientData?.dob ? getAgeFromDob(selectedClientData.dob) : null;

  const selectedClientGestationWeeks = selectedClientData?.menstrual
    ? getGestationWeeksFromLMP(selectedClientData.menstrual)
    : null;

  const isPastAppointment = selectedAppointment
    ? new Date(`${selectedAppointment.date} ${selectedAppointment.startTime}:00`) < new Date()
    : false;

  const isCancelledAppointment = selectedAppointment
    ? selectedAppointment.status === 3
    : false;

  const selectedApptConfirmed = selectedAppointment ? isApptConfirmed(selectedAppointment) : true;
  const selectedApptUnconfirmed = selectedAppointment ? isApptUnconfirmed(selectedAppointment) : false;

  const handleMarkStatus = async (statusValue) => {
    try {
      await updateAppointment(selectedAppointment.id, { status: statusValue });
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Status update failed:', err);
      alert('Failed to update appointment status.');
    }
  };

  const deleteClosure = async id => {
    await deleteDoc(doc(db, 'closures', id));
    fetchData();
    setShowModal(false);
  };

  const deleteUnavailability = async id => {
    await deleteDoc(doc(db, 'noLanguages', id));
    fetchData();
    setShowModal(false);
  };

  const startOfCurrentMonth = startOfMonth(currentDate);
  const endOfCurrentMonth = endOfMonth(currentDate);
  const monthDates = eachDayOfInterval({ start: startOfCurrentMonth, end: endOfCurrentMonth });

  const formatAsPhonePartial = (digits) => {
    const d = digits.replace(/\D/g, '').slice(0, 10);
    const len = d.length;
    if (len === 0) return '';
    if (len < 4) return `(${d}`;
    if (len < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const getDaySpan = () => {
    const slots = getTimeSlots();
    const start = Math.min(...slots);
    const end = Math.max(...slots) + 1;
    return { start, end };
  };

  useEffect(() => {
    if (!currentStaffId) return;
    const messagesRef = collection(db, 'messages');
    const qUnreadToMe = query(
      messagesRef,
      where('to', '==', currentStaffId),
      where('read', '==', null)
    );
    const unsub = onSnapshot(qUnreadToMe, (snap) => {
      const per = {};
      snap.forEach((docSnap) => {
        const m = docSnap.data();
        const fromId = (m.from || '').toString();
        if (
          fromId &&
          fromId !== currentStaffId &&
          fromId.endsWith('STAFF')
        ) {
          per[fromId] = (per[fromId] || 0) + 1;
        }
      });
      setStaffUnreadCounts(per);
      const total = Object.values(per).reduce((a, b) => a + b, 0);
      setStaffUnreadTotal(total);
    });
    return () => unsub();
  }, [currentStaffId]);

  const handleAnnouncementSwipe = (direction) => {
    if (visibleAnnouncements.length <= 1) return;
    setCurrentAnnouncementIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % visibleAnnouncements.length;
      }
      if (direction === 'prev') {
        return (prev - 1 + visibleAnnouncements.length) % visibleAnnouncements.length;
      }
      return prev;
    });
  };

  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const endX = e.changedTouches && e.changedTouches[0]?.clientX;
    if (endX == null) {
      setTouchStartX(null);
      return;
    }
    const deltaX = endX - touchStartX;
    const threshold = 50;
    if (Math.abs(deltaX) > threshold && visibleAnnouncements.length > 1) {
      if (deltaX < 0) {
        handleAnnouncementSwipe('next');
      } else {
        handleAnnouncementSwipe('prev');
      }
    }
    setTouchStartX(null);
  };

  const currentAnnouncement =
    visibleAnnouncements.length > 0
      ? visibleAnnouncements[
      Math.min(currentAnnouncementIndex, visibleAnnouncements.length - 1)
      ]
      : null;

  // Sort announcements for manage modal (newest first by index/date string)
  const sortedAnnouncementsForManage = [...announcements].sort((a, b) => {
    // if date is MM/DD/YYYY, try to sort by parsed date, else leave as-is
    const parseDate = (d) => {
      if (!d) return 0;
      const parts = d.split('/');
      if (parts.length === 3) {
        const [mm, dd, yyyy] = parts.map(Number);
        return new Date(yyyy, mm - 1, dd).getTime();
      }
      const t = Date.parse(d);
      return isNaN(t) ? 0 : t;
    };
    return parseDate(b.date) - parseDate(a.date);
  });

  return (
    <div className="min-h-screen bg-white text-[#5e5e5e]">
      <Navbar
        isStaff={true}
        language="English"
        includeLogout={true}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      {/* Announcements bar (single full-width, carousel with chevrons + swipe) */}
      {currentAnnouncement && (
        <div className="mx-4 mt-4 rounded px-4 py-2 bg-Noshow text-white font-medium relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold uppercase">
              STAFF ANNOUNCEMENT
            </div>
            {visibleAnnouncements.length > 1 && (
              <div className="flex items-center gap-2 text-xs md:text-sm">
                <button
                  onClick={() => handleAnnouncementSwipe('prev')}
                  className="px-1 py-0.5 rounded hover:bg-white/20"
                  aria-label="Previous announcement"
                >
                  ‹
                </button>
                <span>
                  {currentAnnouncementIndex + 1}/{visibleAnnouncements.length}
                </span>
                <button
                  onClick={() => handleAnnouncementSwipe('next')}
                  className="px-1 py-0.5 rounded hover:bg-white/20"
                  aria-label="Next announcement"
                >
                  ›
                </button>
              </div>
            )}
          </div>

          <div
            className="mt-1 flex flex-col md:flex-row md:items-center md:justify-between"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="flex-1 text-center md:text-left">
              <div>
                <span className="font-bold">{currentAnnouncement.body}</span>
                <span className="ml-2">
                  -{currentAnnouncement.sender} ({currentAnnouncement.date})
                </span>
              </div>
            </div>
            <div className="flex gap-2 mt-2 md:mt-0 md:ml-4 justify-center md:justify-end">
              <button
                onClick={async () => {
                  try {
                    if (!currentStaffId) return;
                    await dismissAnnouncementForUser(currentAnnouncement.id, currentStaffId);
                    setAnnouncements(prev =>
                      prev.map(a =>
                        a.id === currentAnnouncement.id
                          ? {
                            ...a,
                            dismissed: [
                              ...(a.dismissed || []),
                              currentStaffId,
                            ],
                          }
                          : a
                      )
                    );
                  } catch (e) {
                    console.error('Failed to dismiss announcement:', e);
                  }
                }}
                className="bg-white text-Noshow rounded px-2 py-1 font-semibold text-xs md:text-sm"
              >
                Dismiss
              </button>

              {userIsSuperstaff && (
                <button
                  onClick={async () => {
                    try {
                      await endAnnouncement(currentAnnouncement.id);
                      const anns = await getAnnouncement();
                      setAnnouncements(anns);
                    } catch (e) {
                      console.error('Failed to end announcement:', e);
                    }
                  }}
                  className="bg-white text-Noshow rounded px-2 py-1 font-semibold text-xs md:text-sm"
                >
                  End
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'Users' && (
        <div className="p-4">
          {/* Title */}
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-2xl font-bold">Users</h1>
          </div>

          <div className="mb-4 flex flex-col gap-3 w-full">
            {/* Buttons group */}
            {userIsSuperstaff && (
              <div className="order-1 md:order-2 flex flex-col gap-2 md:flex-row md:items-center">
                <button
                  onClick={() => {
                    setAnnouncementBodyInput('');
                    setAnnouncementTextStaffInput(true);
                    setShowAnnouncementModal(true);
                  }}
                  className="bg-JMJ hover:bg-JMJGray transition text-white px-4 py-2 rounded font-semibold"
                >
                  Manage Announcements
                </button>

                <button
                  onClick={() => setShowSMSSettingsModal(true)}
                  className="bg-JMJ hover:bg-JMJGray transition text-white px-4 py-2 rounded font-semibold"
                >
                  Staff Notification Settings
                </button>
              </div>
            )}

            <div className="order-2 md:order-2 flex flex-col gap-2 md:flex-row md:items-center">
              {userIsSuperstaff && (
                <button
                  onClick={() => setShowCreateStaffModal(true)}
                  className="bg-JMJ hover:bg-JMJGray transition text-white px-4 py-2 rounded font-semibold"
                >
                  New Staff User
                </button>
              )}

              <button
                onClick={() => setShowCreateClientModal(true)}
                className="bg-JMJ hover:bg-JMJGray transition text-white px-4 py-2 rounded font-semibold"
              >
                New Client User
              </button>
            </div>

            {/* Search bar */}
            <div className="order-2 md:order-1 w-full">
              <input
                type="text"
                placeholder="Search by name or phone"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="bg-white border-2 border-JMJGray placeholder:text-JMJGray text-black px-4 py-2 rounded w-full"
              />
            </div>

            {/* Refresh button and filters */}
            <div className="order-3 md:order-3 flex flex-col md:flex-row gap-2">
              <button
                onClick={fetchData}
                title="Refresh"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded inline-flex items-center gap-2"
              >
                <LuRefreshCw className="w-4 h-4" />
                <span>Refresh Users</span>
              </button>

              <button
                onClick={() => {
                  setUserViewMode(prev => {
                    const nextMode = prev === 'clients' ? 'staff' : 'clients';

                    if (nextMode === 'staff') {
                      setShowNonAbortionMinded(false);
                    }

                    return nextMode;
                  });
                }}
                title="Toggle Staff/Clients View"
                className="bg-JMJ hover:bg-JMJGray transition text-white px-4 py-2 rounded font-semibold inline-flex items-center gap-2"
              >
                <span>
                  {userViewMode === 'staff' ? 'View Clients' : 'View Staff'}
                </span>
              </button>

              {userViewMode === 'clients' && (
                <button
                  onClick={() => setShowNonAbortionMinded(prev => !prev)}
                  title="Show/Hide Non-Abortion-Minded"
                  className="bg-JMJ hover:bg-JMJGray transition text-white px-4 py-2 rounded font-semibold inline-flex items-center gap-2"
                >
                  <span>
                    {showNonAbortionMinded ? 'Hide' : 'Show'} Non-Abortion-Minded Clients
                  </span>
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <StaffUsersTable
              clients={clients}
              filteredClients={filteredClients}
              userIsSuperstaff={userIsSuperstaff}
              getLatestAppointmentStatus={getLatestAppointmentStatus}
              handleDelete={handleDelete}
              handleBlock={handleBlock}
              handleUnblock={handleUnblock}
              setSelectedClient={client => {
                setSelectedClient(client);
                setSelectedClientLatestAppt(getLatestAppointmentForPhone(client?.phone));
              }}
              setClientForm={setClientForm}
              setShowClientModal={setShowClientModal}
              setChatPartner={client => {
                setChatPartner({
                  id: client.id,
                  firstName: client.firstName,
                  lastName: client.lastName,
                  language: client.language
                });
              }}
              setShowChatModal={setShowChatModal}
              setViewStaffUser={client => {
                setViewStaffUser(client);
                setShowStaffInfoModal(true);
              }}
              setShowStaffInfoModal={setShowStaffInfoModal}
              auth={auth}
              starredClients={recentStarredPhones}
              staffUnreadCounts={staffUnreadCounts}
              showNonAbortionMinded={showNonAbortionMinded}
              // NEW props for unconfirmed display
              getLatestAppointmentForPhone={getLatestAppointmentForPhone}
              isApptUnconfirmed={isApptUnconfirmed}
            />
          </div>
        </div>
      )}

      {selectedTab === 'Calendar' && (
        <div className="p-4">
          <div className="flex justify-center items-center mb-4">
            <div className="flex items-center gap-4">
              <span className={`text-[#5e5e5e] ${selectedLocation === 'Orlando' ? 'font-bold' : ''}`}>
                Orlando
              </span>
              <button
                onClick={() =>
                  setSelectedLocation(prev => (prev === 'Orlando' ? 'Kissimmee' : 'Orlando'))
                }
                className="w-14 h-8 flex items-center rounded-full p-1 transition-colors duration-300 bg-JMJ"
              >
                <div
                  className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300
                    ${selectedLocation === 'Kissimmee' ? 'translate-x-6' : 'translate-x-0'}`}
                />
              </button>
              <span className={`text-[#5e5e5e] ${selectedLocation === 'Kissimmee' ? 'font-bold' : ''}`}>
                Kissimmee
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2">
            {/* Desktop / Tablet (md+) */}
            <div className="hidden md:flex flex-col items-center gap-2">
              <button
                onClick={fetchData}
                title="Refresh"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded inline-flex items-center gap-2 mb-3"
              >
                <LuRefreshCw className="w-4 h-4" />
                <span>Refresh Calendar</span>
              </button>

              <div className="flex flex-row justify-center items-center mb-4 gap-3 md:gap-4">
                <button
                  className="bg-JMJ text-white font-bold px-3 py-1 rounded"
                  onClick={() => setCurrentDate(prev => subMonths(prev, 1))}
                  aria-label="Previous Month"
                >
                  ←
                </button>

                <select
                  className="bg-white border-2 border-JMJGray text-black px-3 py-1 rounded"
                  value={currentDate.getMonth()}
                  onChange={(e) => {
                    const newMonth = parseInt(e.target.value, 10);
                    setCurrentDate(prev => new Date(prev.getFullYear(), newMonth, 1));
                  }}
                  aria-label="Select Month"
                >
                  {MONTH_NAMES.map((m, idx) => (
                    <option key={m} value={idx}>{m}</option>
                  ))}
                </select>

                <select
                  className="bg-white border-2 border-JMJGray text-black px-3 py-1 rounded"
                  value={currentDate.getFullYear()}
                  onChange={(e) => {
                    const newYear = parseInt(e.target.value, 10);
                    setCurrentDate(prev => new Date(newYear, prev.getMonth(), 1));
                  }}
                  aria-label="Select Year"
                >
                  {YEAR_OPTIONS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>

                <div className="text-xl font-semibold">
                  {format(currentDate, 'MMMM yyyy')}
                </div>

                <button
                  className="bg-JMJ text-white font-bold px-3 py-1 rounded"
                  onClick={() => setCurrentDate(new Date())}
                >
                  Today
                </button>

                <button
                  className="bg-JMJ text-white font-bold px-3 py-1 rounded"
                  onClick={() => setCurrentDate(prev => addMonths(prev, 1))}
                  aria-label="Next Month"
                >
                  →
                </button>
              </div>
            </div>

            {/* Mobile (sm) */}
            <div className="md:hidden w-full">
              <div className="flex w-full justify-center gap-3 mb-3">
                <select
                  className="flex-1 bg-white border-2 border-JMJGray text-black px-3 py-2 rounded"
                  value={currentDate.getMonth()}
                  onChange={(e) => {
                    const newMonth = parseInt(e.target.value, 10);
                    setCurrentDate(prev => new Date(prev.getFullYear(), newMonth, 1));
                  }}
                  aria-label="Select Month"
                >
                  {MONTH_NAMES.map((m, idx) => (
                    <option key={m} value={idx}>{m}</option>
                  ))}
                </select>

                <select
                  className="w-28 bg-white border-2 border-JMJGray text-black px-3 py-2 rounded"
                  value={currentDate.getFullYear()}
                  onChange={(e) => {
                    const newYear = parseInt(e.target.value, 10);
                    setCurrentDate(prev => new Date(newYear, prev.getMonth(), 1));
                  }}
                  aria-label="Select Year"
                >
                  {YEAR_OPTIONS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-center gap-3 mb-3">
                <button
                  className="bg-JMJ text-white font-bold px-3 py-1 rounded"
                  onClick={() => setCurrentDate(prev => subMonths(prev, 1))}
                  aria-label="Previous Month"
                >
                  ←
                </button>
                <div className="text-lg font-semibold">
                  {format(currentDate, 'MMMM yyyy')}
                </div>
                <button
                  className="bg-JMJ text-white font-bold px-3 py-1 rounded"
                  onClick={() => setCurrentDate(prev => addMonths(prev, 1))}
                  aria-label="Next Month"
                >
                  →
                </button>
              </div>

              <div className="flex items-center justify-center gap-3 mb-4">
                <button
                  className="flex-1 bg-JMJ text-white font-bold px-3 py-2 rounded"
                  onClick={() => setCurrentDate(new Date())}
                >
                  Today
                </button>
                <button
                  onClick={fetchData}
                  title="Refresh"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 py-2 rounded inline-flex items-center justify-center gap-2"
                >
                  <LuRefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>

          <div className={`grid ${gridColsClass} gap-2 mb-2 text-center`}>
            {daysOfWeek.map((day, idx) =>
              relevantDays.includes(idx) ? (
                <div key={idx} className="text-lg font-bold">
                  {day}
                </div>
              ) : null
            )}
          </div>

          <div className={`grid ${gridColsClass} gap-2`}>
            {monthDates
              .filter(date => relevantDays.includes(getDay(date)))
              .reduce((acc, date, index) => {
                if (index === 0) {
                  const firstDayOfMonth = getDay(startOfMonth(currentDate));
                  relevantDays.forEach((day, idx2) => {
                    if (day >= firstDayOfMonth) return;
                    acc.push(<div key={`spacer-${idx2}`} className="p-2" />);
                  });
                }

                acc.push(
                  <div
                    key={date.toISOString()}
                    className="p-2 border-2 border-[#5e5e5e] rounded bg-white"
                  >
                    <div className="text-black text-sm">
                      {format(date, 'MM/dd')}
                      {isToday(date) && (
                        <div className="text-red-400 text-xs font-extrabold">TODAY</div>
                      )}
                    </div>
                    <div>
                      {getTimeSlots().map(hour => {
                        const matchingApp = getAppointmentsForDay(date).find(
                          app => app.startTime === hour
                        );

                        let slotClass = 'bg-JMJGray text-white';
                        let contentNode = `OPEN: ${formatTime(hour)} - ${formatTime(hour + 1)}`;

                        const isClosed = isSlotClosed(date, hour, selectedLocation === 'Orlando' ? 0 : 1);
                        const isNoSpanish = isSpanishUnavailable(date, selectedLocation === 'Orlando' ? 0 : 1);

                        if (isClosed) {
                          slotClass = 'bg-black text-white font-bold';
                          contentNode = `UNAVAILABLE: ${isClosed.reason}`;
                        } else if (isNoSpanish) {
                          slotClass = 'bg-yellow-200 text-black';
                          contentNode = `OPEN: ${formatTime(hour)} - ${formatTime(hour + 1)} (No Spanish)`;
                        }

                        if (matchingApp) {
                          switch (matchingApp.status) {
                            case 0: slotClass = 'bg-Scheduled text-white'; break;
                            case 1: slotClass = 'bg-Show text-white'; break;
                            case 2: slotClass = 'bg-Noshow text-white'; break;
                            case 3: slotClass = 'bg-black text-white'; break;
                            case 4: slotClass = 'bg-Rescheduled text-white'; break;
                            default: break;
                          }

                          const unconfirmed = isApptUnconfirmed(matchingApp);
                          if (unconfirmed) {
                            slotClass = `${slotClass} ring-2 ring-yellow-300`;
                          }

                          const nameLine = `${getClientNameByPhone(matchingApp.client)}: ${formatTime(matchingApp.startTime)} - ${formatTime(matchingApp.endTime)}`;

                          contentNode = unconfirmed ? (
                            <div className="flex flex-col leading-tight">
                              <span className="text-[10px] font-extrabold uppercase">UNCONFIRMED</span>
                              <span className="text-[11px]">{nameLine}</span>
                            </div>
                          ) : (
                            nameLine
                          );
                        }

                        return (
                          <div
                            key={hour}
                            className={`${slotClass} text-xs p-1 my-1 rounded cursor-pointer`}
                            onClick={() => {
                              if (matchingApp) {
                                setSelectedAppointment(matchingApp);
                                setSelectedOpenSlot(null);
                              } else {
                                setSelectedAppointment(null);
                                setSelectedOpenSlot({
                                  date,
                                  hour,
                                  location: selectedLocation === 'Orlando' ? 0 : 1,
                                });
                              }
                              setShowModal(true);
                            }}
                          >
                            {contentNode}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
                return acc;
              }, [])}
          </div>

          {showModal && selectedOpenSlot && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white text-black p-6 rounded shadow-lg w-[90%] max-w-md">
                <h2 className="text-xl font-bold mb-4">Manage Open Slot</h2>
                <p className="mb-2"><strong>Date:</strong> {format(selectedOpenSlot.date, 'MM/dd/yyyy')}</p>
                <p className="mb-4"><strong>Time:</strong> {formatTime(selectedOpenSlot.hour)} - {formatTime(selectedOpenSlot.hour + 1)}</p>
                <div className="flex flex-col gap-2">
                  {matchingClosure ? (
                    <button
                      onClick={() => deleteClosure(matchingClosure.id)}
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                    >
                      Delete Closure
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-black">Reason for Closure</label>
                      <input
                        type="text"
                        value={closureReason}
                        onChange={e => setClosureReason(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-black"
                        placeholder="Enter reason (e.g., holiday, staff meeting)"
                      />
                      <button
                        onClick={async () => {
                          if (!closureReason.trim()) {
                            alert("Please enter a reason for the closure.");
                            return;
                          }
                          await addDoc(collection(db, 'closures'), {
                            date: format(selectedOpenSlot.date, 'MM/dd/yyyy'),
                            startTime: selectedOpenSlot.hour,
                            endTime: selectedOpenSlot.hour + 1,
                            location: selectedOpenSlot.location,
                            reason: `"${closureReason}" -${auth.client?.firstName} ${auth.client?.lastName}`,
                          });

                          setClosureReason('');
                          setShowModal(false);
                          fetchData();
                        }}
                        className="bg-black hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded"
                      >
                        Add Hour Closure
                      </button>

                      {!hasAppointmentsOnDateAtLocation(selectedOpenSlot.date, selectedOpenSlot.location) && (
                        <button
                          onClick={async () => {
                            if (!closureReason.trim()) {
                              alert("Please enter a reason for the closure.");
                              return;
                            }
                            const { start, end } = getDaySpan();
                            await addDoc(collection(db, 'closures'), {
                              date: format(selectedOpenSlot.date, 'MM/dd/yyyy'),
                              startTime: start,
                              endTime: end,
                              location: selectedOpenSlot.location,
                              reason: `"${closureReason}" -${auth.client?.firstName} ${auth.client?.lastName}`,
                            });

                            setClosureReason('');
                            setShowModal(false);
                            fetchData();
                          }}
                          className="bg-black hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded"
                          title="Create a single closure that covers every time slot this day"
                        >
                          Add All-Day Closure
                        </button>
                      )}
                    </div>
                  )}
                  {matchingUnavailability ? (
                    <button
                      onClick={() => deleteUnavailability(matchingUnavailability.id)}
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                    >
                      Delete Spanish Unavailability
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        await addDoc(collection(db, 'noLanguages'), {
                          date: format(selectedOpenSlot.date, 'MM/dd/yyyy'),
                          location: selectedOpenSlot.location,
                          language: 'Spanish',
                        });
                        setShowModal(false);
                        fetchData();
                      }}
                      className="bg-yellow-200 hover:bg-yellow-400 text-black font-semibold py-2 px-4 rounded"
                    >
                      Add All-Day Spanish Unavailability
                    </button>
                  )}
                  <button
                    onClick={() => setShowModal(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-black font-semibold py-2 px-4 rounded"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {showModal && selectedAppointment && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white text-black p-6 rounded shadow-lg w-[90%] max-w-md">
                <h2 className="text-xl font-bold mb-4">Appointment Info</h2>

                <p className="mb-2"><strong>Location:</strong> {locationName}</p>
                <p className="mb-2"><strong>Date:</strong> {selectedAppointment.date}</p>
                <p className="mb-2">
                  <strong>Time:</strong> {formatTime(selectedAppointment.startTime)} - {formatTime(selectedAppointment.endTime)}
                </p>

                <p className="mb-2">
                  <strong>Client Confirmed:</strong> {selectedApptConfirmed ? 'Yes' : 'No'}
                </p>

                {selectedApptUnconfirmed && (
                  <div className="my-3 rounded bg-yellow-100 text-yellow-900 font-bold text-center p-2">
                    UNCONFIRMED — waiting for client text verification
                  </div>
                )}

                <p className="mb-2"><strong>Staff Notes:</strong> {selectedClientData?.adminNotes || 'N/A'}</p>

                <hr className="my-3 border-gray-300" />

                <p className="mb-2">
                  <strong>Client:</strong> {getClientNameByPhone(selectedAppointment.client)}
                  {selectedClientAge !== null ? ` (age ${selectedClientAge})` : ''}
                </p>
                <p className="mb-2">
                  <strong>DOB:</strong>{' '}
                  {selectedClientData?.dob || 'N/A'}
                  {selectedClientAge !== null && (
                    <> ({selectedClientAge} years old)</>
                  )}
                </p>

                <p className="mb-2">
                  <strong>LMP:</strong>{' '}
                  {selectedClientData?.menstrual || 'N/A'}
                  {selectedClientGestationWeeks !== null && (
                    <> (gestation {selectedClientGestationWeeks.toFixed(1)})</>
                  )}
                  {selectedClientGestationWeeks !== null && selectedClientGestationWeeks > 6 && (
                    <span className="text-red-600 ml-1">★</span>
                  )}
                </p>

                <p className="mb-2"><strong>Phone:</strong> {formatPhoneNumber(selectedClientData?.phone) || 'N/A'}</p>

                <p className="mb-2"><strong>Language:</strong> {selectedClientData?.language || 'N/A'}</p>

                <p className="mb-2"><strong>Client Notes:</strong> {selectedClientData?.notes || 'N/A'}</p>
                {selectedAppointment?.rescheduleReason && selectedAppointment.rescheduleReason.trim() !== '' && (
                  <p className="mb-2">
                    <strong>Reschedule/Cancellation Reason:</strong> {selectedAppointment.rescheduleReason}
                  </p>
                )}

                <div className="flex flex-col gap-2 mt-2">
                  {isCancelledAppointment ? (
                    <button
                      onClick={handleDeleteAndFreeSlot}
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                    >
                      Clear Cancelled Appointment
                    </button>
                  ) : isPastAppointment ? (
                    <>
                      <button
                        onClick={() => handleMarkStatus(1)}
                        className="bg-Show hover:bg-green-700 text-white font-semibold py-2 px-4 rounded"
                      >
                        Mark as Show
                      </button>
                      <button
                        onClick={() => handleMarkStatus(2)}
                        className="bg-Noshow hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                      >
                        Mark as No-Show
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleDeleteAppointment}
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                    >
                      Cancel Appointment
                    </button>
                  )}

                  {selectedClientData && (
                    <button
                      onClick={() => {
                        setChatPartner({
                          id: selectedClientData.id,
                          firstName: selectedClientData.firstName,
                          lastName: selectedClientData.lastName,
                          language: selectedClientData.language
                        });
                        setShowChatModal(true);
                        setShowModal(false);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
                    >
                      Chat
                    </button>
                  )}

                  <button
                    onClick={() => setShowModal(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-black font-semibold py-2 px-4 rounded"
                  >
                    Close
                  </button>
                </div>

              </div>
            </div>
          )}
        </div>
      )}

      {showClientModal && selectedClient && (
        <ClientInfoModal
          clientForm={clientForm}
          setClientForm={setClientForm}
          onSave={handleClientUpdate}
          onClose={() => setShowClientModal(false)}
          latestAppointment={selectedClientLatestAppt}
          isApptUnconfirmed={isApptUnconfirmed}
        />
      )}

      {showCreateStaffModal && (
        <CreateStaffModal
          newStaffFirstName={newStaffFirstName}
          setNewStaffFirstName={setNewStaffFirstName}
          newStaffLastName={newStaffLastName}
          setNewStaffLastName={setNewStaffLastName}
          newStaffPhone={newStaffPhone}
          setNewStaffPhone={setNewStaffPhone}
          newStaffPassword={newStaffPassword}
          setNewStaffPassword={setNewStaffPassword}
          newStaffSMSList={newStaffSMSList}
          setNewStaffSMSList={setNewStaffSMSList}
          formatAsPhonePartial={formatAsPhonePartial}
          onCancel={() => setShowCreateStaffModal(false)}
          onCreate={async () => {
            try {
              const staffId = `${newStaffPhone}STAFF`;
              await setDoc(doc(db, 'clients', staffId), {
                phone: newStaffPhone.toString(),
                password: newStaffPassword,
                superstaff: false,
                firstName: newStaffFirstName,
                lastName: newStaffLastName,
                SMSlist: newStaffSMSList,
              });
              alert('Staff user created!');
              setShowCreateStaffModal(false);
              setNewStaffPhone('');
              setNewStaffPassword('');
              setNewStaffFirstName('');
              setNewStaffLastName('');
              fetchData();
            } catch (err) {
              console.error('Failed to create staff user:', err);
              alert('Error creating staff user.');
            }
          }}
        />
      )}

      {showCreateClientModal && (
        <CreateClientModal
          newClientForm={newClientForm}
          setNewClientForm={setNewClientForm}
          formatAsPhonePartial={formatAsPhonePartial}
          isCreating={creatingClient}
          onCancel={() => {
            if (creatingClient) return;
            setShowCreateClientModal(false);
            setNewClientForm(getEmptyNewClientForm());
          }}
          onCreate={async () => {
            const formattedPhone = ('' + newClientForm.phone).replace(/\D/g, '');
            const clientRef = doc(db, 'clients', formattedPhone);

            try {
              const existingSnap = await getDoc(clientRef);
              if (existingSnap.exists()) {
                alert('A client with that phone number already exists.');
                return;
              }

              setCreatingClient(true);

              const language = newClientForm.language || 'English';

              await createNewClient(formattedPhone);

              await updateClient(formattedPhone, {
                phone: formattedPhone,
                firstName: normalizeName(newClientForm.firstName),
                lastName: normalizeName(newClientForm.lastName),
                email: newClientForm.email.trim(),
                language,
                dob: isoToSlashes(newClientForm.dob),
                menstrual: isoToSlashes(newClientForm.menstrual),
                adminNotes: newClientForm.adminNotes || '',
                notes: newClientForm.notes || '',
                verified: true,
                verificationToken: '',
                pendingAppointmentId: '',
                createdByStaff: true,
              });

              // Close/reset immediately after successful creation
              setShowCreateClientModal(false);
              setNewClientForm(getEmptyNewClientForm());
              await fetchData();

              // Send disclaimer + login link text
              try {
                await sendConfidentialityDisclaimer(formattedPhone, language);

                const loginMessage =
                  language === 'Spanish'
                    ? 'JMJPC BOOKING: Su cuenta ha sido creada. Inicie sesión aquí: https://jmjpcbooking.org/#/login'
                    : 'JMJPC BOOKING: Your account has been created. Please log in here: https://jmjpcbooking.org/#/login';

                await sendTextMessage({
                  message: loginMessage,
                  yourPhoneNumber: '4073059084',
                  recipientPhoneNumber: formattedPhone,
                  senderName: 'JMJPC',
                });

                alert('Client user created and login text sent!');
              } catch (textErr) {
                console.error('Client created, but text failed:', textErr);
                alert('Client user created, but the text message failed to send.');
              }
            } catch (err) {
              console.error('Failed to create client user:', err);
              alert('Error creating client user.');
            } finally {
              setCreatingClient(false);
            }
          }}
        />
      )}

      {showChatModal && chatPartner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-4 max-w-2xl w-full relative">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold text-black">
                Chat with {chatPartner.firstName} {chatPartner.lastName}
              </h2>
              <button
                onClick={() => setShowChatModal(false)}
                className="text-black text-xl font-bold px-2"
              >
                ×
              </button>
            </div>
            <UnifiedChatbox
              fromUser={{
                id: auth.client?.id || `${auth.client?.phone}STAFF`,
                firstName: auth.client?.firstName || '',
                lastName: auth.client?.lastName || '',
                language: auth.client?.language || 'English',
              }}
              toUser={{
                id: chatPartner.id,
                firstName: chatPartner.firstName,
                lastName: chatPartner.lastName,
                language: chatPartner.language || 'English',
              }}
            />
          </div>
        </div>
      )}

      {showStaffInfoModal && viewStaffUser && (
        <StaffInfoModal
          viewStaffUser={viewStaffUser}
          userIsSuperstaff={userIsSuperstaff}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          onClose={() => {
            setShowStaffInfoModal(false);
            setShowPassword(false);
            setViewStaffUser(null);
          }}
          formatPhoneNumber={formatPhoneNumber}
          fetchData={fetchData}
        />
      )}

      {/* MANAGE ANNOUNCEMENTS MODAL */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Manage Announcements</h2>

            {/* Create new announcement section */}
            <div className="mb-6 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-sm uppercase tracking-wide">
                Create New Announcement
              </h3>
              <label className="block mb-2 text-sm font-medium">
                Body:
                <textarea
                  value={announcementBodyInput}
                  onChange={e => setAnnouncementBodyInput(e.target.value)}
                  className="w-full border rounded p-2 mt-1 text-sm"
                  rows={3}
                  placeholder="Type your staff announcement here..."
                />
              </label>
              <label className="flex items-center mb-4 text-sm">
                <input
                  type="checkbox"
                  checked={announcementTextStaffInput}
                  onChange={e => setAnnouncementTextStaffInput(e.target.checked)}
                  className="mr-2"
                />
                Text this announcement to all Staff when saved?
              </label>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setAnnouncementBodyInput('');
                    setAnnouncementTextStaffInput(true);
                  }}
                  className="bg-gray-200 hover:bg-gray-300 text-black font-semibold py-1.5 px-3 rounded text-sm"
                  disabled={savingAnnouncement}
                >
                  Clear
                </button>
                <button
                  onClick={async () => {
                    try {
                      if (!announcementBodyInput.trim()) {
                        alert('Please enter a body for the announcement.');
                        return;
                      }
                      setSavingAnnouncement(true);
                      const formattedDate = new Date();
                      const mm = String(formattedDate.getMonth() + 1).padStart(2, '0');
                      const dd = String(formattedDate.getDate()).padStart(2, '0');
                      const yyyy = formattedDate.getFullYear();
                      const dateStr = `${mm}/${dd}/${yyyy}`;
                      const senderName = `${auth.client?.firstName || ''} ${auth.client?.lastName || ''}`.trim();

                      await newAnnouncement({
                        body: announcementBodyInput,
                        date: dateStr,
                        sender: senderName,
                        textStaff: announcementTextStaffInput
                      });
                      setAnnouncementTextStaffInput(false);
                      setAnnouncementBodyInput('');

                      const anns = await getAnnouncement();
                      setAnnouncements(anns);
                    } catch (e) {
                      console.error('Failed to create announcement:', e);
                      alert('Failed to create announcement.');
                    } finally {
                      setSavingAnnouncement(false);
                    }
                  }}
                  className="bg-JMJ hover:bg-JMJGray text-white font-semibold py-1.5 px-4 rounded flex items-center gap-2 text-sm"
                  disabled={savingAnnouncement}
                >
                  {savingAnnouncement ? (
                    <>
                      <LuRefreshCw className="animate-spin w-4 h-4" />
                      Saving...
                    </>
                  ) : (
                    'Save Announcement'
                  )}
                </button>
              </div>
            </div>

            {/* Existing announcements section */}
            <div className="mb-4">
              <h3 className="font-semibold mb-2 text-sm uppercase tracking-wide">
                Existing Announcements
              </h3>
              {sortedAnnouncementsForManage.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No announcements have been created yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {sortedAnnouncementsForManage.map((ann) => {
                    const isEnded = ann.visible === false;
                    return (
                      <div
                        key={ann.id}
                        className={`border rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 ${isEnded ? 'bg-gray-100 text-gray-600' : 'bg-white'
                          }`}
                      >
                        <div className="flex-1 text-sm">
                          <div className="font-semibold">
                            {ann.body}
                          </div>
                          <div className="text-xs mt-0.5">
                            <span>
                              -{ann.sender} ({ann.date})
                            </span>
                            {ann.textStaff && (
                              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[11px] font-semibold">
                                SMS
                              </span>
                            )}
                            {isEnded && (
                              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-gray-300 text-gray-800 text-[11px] font-semibold">
                                Ended
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          {/* Treat "delete" as permanent end via endAnnouncement */}
                          <button
                            onClick={async () => {
                              const confirmed = window.confirm(
                                'Are you sure you want to delete this announcement for all staff?'
                              );
                              if (!confirmed) return;
                              try {
                                await endAnnouncement(ann.id);
                                const anns = await getAnnouncement();
                                setAnnouncements(anns);
                              } catch (e) {
                                console.error('Failed to delete announcement:', e);
                                alert('Failed to delete announcement.');
                              }
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-2 py-1 rounded text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowAnnouncementModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-black font-semibold py-2 px-4 rounded text-sm"
                disabled={savingAnnouncement}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showSMSSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
            <h2 className="text-xl font-bold mb-4">Staff Notification Settings</h2>
            <p className="mb-2 text-sm">
              Select who receives an SMS message when a Client books an appointment, cancels an appointment, reschedules an appointment, or sends a message to JMJPC.
            </p>
            <p className="mb-2 text-sm">
              Regardless of this setting, all Staff users will receive an SMS message when messaged by another Staff user. Announcements that have the Text option selected will also be sent to all Staff users regardless of this setting.
            </p>
            <div className="mb-2">
              <span className="font-bold text-sm">Live:</span>{' '}
              <span className="text-sm">
                Staff users on the SMS List receive SMS alerts when a Client books, cancels, reschedules, or sends a message to JMJPC. Admins can manage this list via the "Info" button next to a Staff user. Certain Staff email addresses also receive these notifications by email when enabled.
              </span>
            </div>
            <div className="mb-4">
              <span className="font-bold text-sm">Development:</span>{' '}
              <span className="text-sm">
                Only developers and select testers receive SMS alerts when a Client books, cancels, reschedules, or sends a message to JMJPC. This list is managed by the development team and not acessible in this app. <strong>Use only during active development or testing.</strong> No email notifications are sent in this mode.
              </span>
            </div>
            <div className="mb-6">
              <div className="flex gap-2">
                <button
                  onClick={() => setSmsRecipientGroup('staff')}
                  className={`flex-1 py-2 rounded ${smsRecipientGroup === 'staff' ? 'bg-black text-white font-bold' : 'bg-gray-300 hover:bg-gray-400 text-black'}`}
                >
                  Live
                </button>
                <button
                  onClick={() => setSmsRecipientGroup('development')}
                  className={`flex-1 py-2 rounded ${smsRecipientGroup === 'development' ? 'bg-black text-white font-bold' : 'bg-gray-300 hover:bg-gray-400 text-black'}`}
                >
                  Development
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSMSSettingsModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-black font-semibold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const group = smsRecipientGroup === 'development' ? 'development' : 'staff';
                    await updateSMSNotificationGroup(group);
                  } catch (e) {
                    console.error('Failed to save SMS settings:', e);
                    alert('Failed to save staff notification settings.');
                  } finally {
                    setShowSMSSettingsModal(false);
                  }
                }}
                className="bg-JMJ hover:bg-JMJGray text-white font-semibold py-2 px-4 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Staff;
